/**
 * A ação em lote sobre os pinouts marcados — o padrão 07.
 *
 * Nove alterações e a exclusão, todas do endpoint. A regra 6 da padronização
 * manda: *toda ação em lote mostra a contagem antes e permite desfazer depois.*
 *
 * A contagem antes é fácil. **O desfazer exige um truque**, porque o endpoint de
 * lote grava um valor só para todos os ids e não devolve os anteriores. Só que a
 * grade tem os valores anteriores em mãos — são as linhas na tela. Então o
 * desfazer agrupa as linhas pelo valor que elas tinham e manda uma chamada por
 * valor distinto: se 40 linhas tinham três cores diferentes, são três chamadas
 * para devolver cada grupo à sua cor.
 *
 * A **exclusão é a exceção honesta**: linha apagada não volta, e o endpoint não
 * devolve o que apagou. Ali a confirmação é a única rede, e por isso ela é mais
 * dura — exige ler a contagem antes de liberar o botão.
 */

import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/api/cliente"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { chaves } from "@/api/query"
import {
  usarChicotes, usarComponentes, usarConectores, usarCores, usarRestricoes, type Catalogo,
} from "@/api/catalogos"
import type { PinoutResponse } from "@/api/tipos"
import { CampoDeBusca, idDe, VAZIO, type ValorBusca } from "@/kit/CampoDeBusca"
import { TIPOS_PINOUT } from "./tipos"

/**
 * O valor escolhido: texto ou id nas listas, e no conector o campo de busca —
 * que pode trazer um conector da lista ou um nome novo.
 */
type ValorDoLote = string | number | ValorBusca

/** Uma operação de lote: a rota, o rótulo, e como ler o valor anterior de uma linha. */
type Operacao = {
  chave: string
  rotulo: string
  rota: string
  /** Como montar o corpo a partir do valor escolhido. */
  corpo: (valor: ValorDoLote) => Record<string, unknown>
  /** O valor que a linha tinha — é o que torna o desfazer possível. */
  anterior?: (p: PinoutResponse) => string | number
  /** De onde sai a lista de valores possíveis. */
  fonte?: "componentes" | "conectores" | "cores" | "chicotes" | "restricoes" | "tipos" | "texto"
}

const OPERACOES: Operacao[] = [
  {
    chave: "cor", rotulo: "Cor", rota: "api/pinouts/lote/cor", fonte: "cores",
    corpo: (v) => ({ corId: Number(v) }), anterior: (p) => p.corId,
  },
  {
    chave: "chicote", rotulo: "Chicote", rota: "api/pinouts/lote/chicote", fonte: "chicotes",
    corpo: (v) => ({ chicoteId: Number(v) }), anterior: (p) => p.chicoteId,
  },
  {
    chave: "tipo", rotulo: "Tipo", rota: "api/pinouts/lote/tipo", fonte: "tipos",
    corpo: (v) => ({ tipo: Number(v) }), anterior: (p) => p.tipo,
  },
  {
    chave: "restricoes", rotulo: "Restrição", rota: "api/pinouts/lote/restricoes", fonte: "restricoes",
    corpo: (v) => ({ restricaoId: Number(v), restricao: "" }),
    // Restrição é lista no pinout; o lote grava uma. Desfazer não é confiável aqui.
  },
  {
    // Trocar o componente leva o conector de cada linha junto, pelo nome — antes
    // ele era zerado. Desfazer devolve o componente, e o conector volta com ele.
    chave: "componente", rotulo: "Componente", rota: "api/pinouts/lote/componente", fonte: "componentes",
    corpo: (v) => ({ componenteId: Number(v) }), anterior: (p) => p.componenteId,
  },
  {
    // O conector é do componente: a API grava, em cada linha, o conector com esse
    // nome no componente dela, e cria se faltar. O legado obrigava a escolher
    // componente e conector juntos; aqui cada um tem o seu botão, como os outros.
    chave: "conector", rotulo: "Conector", rota: "api/pinouts/lote/conector", fonte: "conectores",
    corpo: (v) => typeof v === "object"
      ? { conectorId: idDe(v), conector: v.estado === "novo" ? v.texto : "" }
      : { conectorId: Number(v), conector: "" },
    anterior: (p) => p.conectorId,
  },
  {
    chave: "obs", rotulo: "Observação", rota: "api/pinouts/lote/obs", fonte: "texto",
    corpo: (v) => ({ obs: String(v) }), anterior: (p) => p.obs,
  },
  {
    chave: "arquivo-original", rotulo: "Fonte", rota: "api/pinouts/lote/arquivo-original", fonte: "texto",
    corpo: (v) => ({ arquivoOriginal: String(v) }), anterior: (p) => p.arquivoOriginal,
  },
  {
    chave: "circuito", rotulo: "Circuito", rota: "api/pinouts/lote/circuito", fonte: "texto",
    corpo: (v) => ({ circuitoId: Number(v) }), anterior: (p) => p.circuitoId,
  },
]

type Feito = {
  operacao: Operacao
  afetados: number
  /** valor anterior → ids que tinham esse valor. O que permite voltar. */
  anteriores: Map<string | number, number[]>
  quando: number
}

export function AcaoEmLote({
  projetoId, marcados, linhas, aoLimpar, aoDuplicar,
}: {
  projetoId: number
  marcados: Set<number>
  /** As linhas da página, para ler o valor anterior de cada marcada. */
  linhas: PinoutResponse[]
  aoLimpar: () => void
  /** Abre o lançamento preenchido com as marcadas, para gravar como pinouts novos. */
  aoDuplicar?: (linhas: PinoutResponse[]) => void
}) {
  const qc = useQueryClient()
  const ir = useNavigate()
  const [aberta, setAberta] = useState<Operacao | null>(null)
  const [valor, setValor] = useState<string>("")
  const [conector, setConector] = useState<ValorBusca>(VAZIO)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false)
  const [feito, setFeito] = useState<Feito | null>(null)

  const ids = useMemo(() => [...marcados], [marcados])
  const marcadasNaPagina = useMemo(
    () => linhas.filter((l) => marcados.has(l.id)),
    [linhas, marcados],
  )
  const componentesMarcados = useMemo(
    () => [...new Set(marcadasNaPagina.map((l) => l.componenteId))],
    [marcadasNaPagina],
  )

  const componentes = usarComponentes(projetoId)
  // Os conectores do componente das marcadas; sendo de vários, os do projeto.
  const conectores = usarConectores(projetoId, componentesMarcados.length === 1 ? componentesMarcados[0] : undefined)
  const cores = usarCores(projetoId)
  const chicotes = usarChicotes(projetoId)
  const restricoes = usarRestricoes(projetoId)

  function abrir(op: Operacao | null) {
    setAberta(op)
    setValor("")
    setConector(VAZIO)
  }

  function invalidar() {
    qc.invalidateQueries({ queryKey: ["pinouts", projetoId] })
    qc.invalidateQueries({ queryKey: ["catalogo"] })
    qc.invalidateQueries({ queryKey: chaves.projeto(projetoId) })
  }

  const aplicar = useMutation({
    mutationFn: async ({ op, v }: { op: Operacao; v: ValorDoLote }) => {
      const r = await api.post<{ afetados: number }>(
        op.rota, { ids, ...op.corpo(v) }, { params: { projetoId } },
      )
      return r.data
    },
    onSuccess: (r, { op }) => {
      // Guarda os valores anteriores agrupados: é o que o desfazer vai usar.
      const anteriores = new Map<string | number, number[]>()
      if (op.anterior) {
        for (const l of marcadasNaPagina) {
          const antes = op.anterior(l)
          const lista = anteriores.get(antes) ?? []
          lista.push(l.id)
          anteriores.set(antes, lista)
        }
      }
      setFeito({ operacao: op, afetados: r.afetados, anteriores, quando: Date.now() })
      abrir(null)
      invalidar()
    },
  })

  const desfazer = useMutation({
    mutationFn: async (f: Feito) => {
      // Uma chamada por valor distinto: 40 linhas com três cores viram três
      // chamadas, cada uma devolvendo o seu grupo à cor que tinha.
      for (const [antes, grupo] of f.anteriores) {
        await api.post(f.operacao.rota, { ids: grupo, ...f.operacao.corpo(antes) }, { params: { projetoId } })
      }
    },
    onSuccess: () => { setFeito(null); invalidar() },
  })

  const excluir = useMutation({
    mutationFn: async () => {
      const r = await api.post<{ excluidos: number }>(
        "api/pinouts/excluir-selecionados", { ids }, { params: { projetoId } },
      )
      return r.data
    },
    onSuccess: () => { setConfirmandoExclusao(false); aoLimpar(); invalidar() },
  })

  const quantos = ids.length
  const foraDaPagina = quantos - marcadasNaPagina.length

  // O desenho é sempre de UM componente. Se as linhas marcadas são de vários, a
  // pergunta "de qual?" não tem resposta e o botão explica em vez de escolher
  // sozinho — escolher sozinho daria um desenho parcial que parece completo.
  const podeDesenhar = componentesMarcados.length === 1 && foraDaPagina === 0
  const erro = aplicar.error ?? excluir.error ?? desfazer.error

  return (
    <div
      style={{
        flex: "0 0 auto", display: "flex", flexDirection: "column",
        background: "var(--brand-soft)", borderBottom: "1px solid var(--brand)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 18px", flexWrap: "wrap" }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--brand-on-soft)" }}>
          {quantos} {quantos === 1 ? "linha marcada" : "linhas marcadas"}
        </span>

        <button onClick={aoLimpar} style={{ fontSize: 11.5, color: "var(--brand-on-soft)", opacity: 0.8 }}>
          desmarcar
        </button>

        <span style={{ width: 1, height: 18, background: "var(--brand)", opacity: 0.3, margin: "0 2px" }} />

        {OPERACOES.map((op) => (
          <button
            key={op.chave}
            onClick={() => abrir(aberta?.chave === op.chave ? null : op)}
            aria-pressed={aberta?.chave === op.chave}
            style={{
              height: 26, padding: "0 10px", borderRadius: 6, fontSize: 12,
              fontWeight: aberta?.chave === op.chave ? 600 : 500,
              background: aberta?.chave === op.chave ? "var(--surface)" : "transparent",
              color: "var(--brand-on-soft)",
              border: `1px solid ${aberta?.chave === op.chave ? "var(--brand)" : "transparent"}`,
            }}
          >
            {op.rotulo}
          </button>
        ))}

        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {feito && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12 }}>
              <span style={{ color: "var(--ok-on-soft)", fontWeight: 600 }}>
                {feito.afetados} {feito.afetados === 1 ? "alterada" : "alteradas"}
              </span>
              {feito.anteriores.size > 0 ? (
                <button
                  onClick={() => desfazer.mutate(feito)}
                  disabled={desfazer.isPending}
                  style={{ fontSize: 12, fontWeight: 600, color: "var(--brand-on-soft)" }}
                >
                  {desfazer.isPending
                    ? "desfazendo…"
                    : `desfazer (${feito.anteriores.size} ${feito.anteriores.size === 1 ? "valor" : "valores"})`}
                </button>
              ) : (
                <span title="Esta operação não guarda o valor anterior de forma confiável."
                      style={{ fontSize: 11, color: "var(--txt3)" }}>
                  sem desfazer
                </span>
              )}
            </span>
          )}

          <button
            onClick={() => ir(`../grafico?pinouts=${ids.join(",")}&componenteId=${componentesMarcados[0]}`)}
            disabled={!podeDesenhar}
            title={
              podeDesenhar
                ? "Desenha só as linhas marcadas"
                : componentesMarcados.length > 1
                  ? `As marcadas são de ${componentesMarcados.length} componentes — o desenho é de um só.`
                  : "Há marcadas fora desta página; o desenho usa as que estão à vista."
            }
            style={{
              height: 26, padding: "0 10px", borderRadius: 6, fontSize: 12, fontWeight: 600,
              color: "var(--txt2)", border: "1px solid var(--line2)", background: "var(--surface)",
              opacity: podeDesenhar ? 1 : 0.45, cursor: podeDesenhar ? "pointer" : "default",
            }}
          >
            Ver gráfico
          </button>

          {aoDuplicar && (
            <button
              onClick={() => aoDuplicar(marcadasNaPagina)}
              disabled={foraDaPagina > 0}
              title={foraDaPagina > 0
                ? "Há marcadas fora desta página; duplique as que estão à vista."
                : "Abre o lançamento preenchido com as marcadas — mude o que for diferente e grave como pinouts novos"}
              style={{
                height: 26, padding: "0 10px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                color: "var(--txt2)", border: "1px solid var(--line2)", background: "var(--surface)",
                opacity: foraDaPagina > 0 ? 0.45 : 1, cursor: foraDaPagina > 0 ? "default" : "pointer",
              }}
            >
              Duplicar
            </button>
          )}

          <button
            onClick={() => setConfirmandoExclusao(true)}
            style={{
              height: 26, padding: "0 10px", borderRadius: 6, fontSize: 12, fontWeight: 600,
              color: "var(--erro)", border: "1px solid var(--erro)", background: "var(--surface)",
            }}
          >
            Excluir
          </button>
        </span>
      </div>

      {aberta && (
        <Escolher
          operacao={aberta}
          valor={valor}
          aoMudar={setValor}
          conector={conector}
          aoMudarConector={setConector}
          componentesMarcados={componentesMarcados.length}
          quantos={quantos}
          foraDaPagina={foraDaPagina}
          aplicando={aplicar.isPending}
          aoAplicar={() => aplicar.mutate({ op: aberta, v: aberta.fonte === "conectores" ? conector : valor })}
          aoCancelar={() => abrir(null)}
          catalogos={{ componentes, conectores, cores, chicotes, restricoes }}
        />
      )}

      {confirmandoExclusao && (
        <ConfirmarExclusao
          quantos={quantos}
          excluindo={excluir.isPending}
          aoExcluir={() => excluir.mutate()}
          aoCancelar={() => setConfirmandoExclusao(false)}
        />
      )}

      {erro != null && (
        <div style={{
          padding: "8px 18px", fontSize: 11.5, lineHeight: 1.5,
          background: erro instanceof ErroDeTrava ? "var(--acao-soft)" : "var(--erro-soft)",
          color: erro instanceof ErroDeTrava ? "var(--acao-on-soft)" : "var(--erro-on-soft)",
        }}>
          {erro instanceof ErroDeTrava
            ? <><b>Bloqueado pela trava do canário</b> — {erro.message}</>
            : erro instanceof ErroDaApi ? erro.message : "Não foi possível aplicar."}
        </div>
      )}
    </div>
  )
}

function Escolher({
  operacao, valor, aoMudar, conector, aoMudarConector, componentesMarcados,
  quantos, foraDaPagina, aplicando, aoAplicar, aoCancelar, catalogos,
}: {
  operacao: Operacao
  valor: string
  aoMudar: (v: string) => void
  conector: ValorBusca
  aoMudarConector: (v: ValorBusca) => void
  /** De quantos componentes são as marcadas — o conector é de cada um. */
  componentesMarcados: number
  quantos: number
  foraDaPagina: number
  aplicando: boolean
  aoAplicar: () => void
  aoCancelar: () => void
  catalogos: Record<string, Catalogo>
}) {
  const catalogo =
    operacao.fonte && operacao.fonte !== "tipos" && operacao.fonte !== "texto"
      ? catalogos[operacao.fonte]
      : undefined

  const podeAplicar = operacao.fonte === "conectores"
    ? conector.estado !== "vazio"
    : valor.trim() !== "" || operacao.fonte === "texto"

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, padding: "9px 18px",
      background: "var(--surface)", borderTop: "1px solid var(--brand)",
    }}>
      <span style={{ fontSize: 12, color: "var(--txt2)" }}>
        {operacao.rotulo} de <b style={{ color: "var(--txt)" }}>{quantos}</b>{" "}
        {quantos === 1 ? "linha" : "linhas"} para
      </span>

      {operacao.fonte === "texto" ? (
        <input
          value={valor}
          autoFocus
          onChange={(e) => aoMudar(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && podeAplicar) aoAplicar() }}
          placeholder={operacao.chave === "circuito" ? "id do circuito" : "(deixe vazio para limpar)"}
          style={{
            width: 220, height: 28, padding: "0 9px", borderRadius: 7,
            border: "1px solid var(--line2)", background: "var(--surface2)", fontSize: 12.5,
          }}
        />
      ) : operacao.fonte === "conectores" ? (
        <>
          <div style={{ width: 220 }}>
            <CampoDeBusca
              rotulo="Conector" mono dica="conector (ex.: E100-K)" id="lote-conector"
              valor={conector} aoMudar={aoMudarConector}
              fonte={{ modo: "memoria", itens: catalogo?.itens ?? [], carregando: catalogo?.carregando ?? false }}
            />
          </div>
          {componentesMarcados > 1 && (
            <span style={{ fontSize: 11.5, color: "var(--txt2)" }}>
              São {componentesMarcados} componentes: cada linha fica com o conector desse nome no componente dela.
            </span>
          )}
        </>
      ) : operacao.fonte === "tipos" ? (
        <select value={valor} onChange={(e) => aoMudar(e.target.value)} autoFocus
                style={{ height: 28, padding: "0 8px", borderRadius: 7, border: "1px solid var(--line2)", fontSize: 12.5 }}>
          <option value="">escolha…</option>
          {Object.entries(TIPOS_PINOUT).map(([v, r]) => <option key={v} value={v}>{r}</option>)}
        </select>
      ) : (
        <select value={valor} onChange={(e) => aoMudar(e.target.value)} autoFocus
                style={{ height: 28, padding: "0 8px", borderRadius: 7, border: "1px solid var(--line2)", fontSize: 12.5, maxWidth: 260 }}>
          <option value="">escolha…</option>
          {(catalogo?.itens ?? []).map((i) => (
            <option key={i.id} value={i.id}>{i.codigo ? `${i.codigo} — ${i.rotulo}` : i.rotulo}</option>
          ))}
        </select>
      )}

      {/* Marcação que atravessa páginas é o jeito mais fácil de alterar demais
          sem perceber. Se houver linha marcada fora desta página, avisa. */}
      {foraDaPagina > 0 && (
        <span style={{ fontSize: 11.5, color: "var(--acao-on-soft)" }}>
          {foraDaPagina} {foraDaPagina === 1 ? "marcada está" : "marcadas estão"} fora desta página —
          o desfazer só devolve as que estão à vista.
        </span>
      )}

      <span style={{ flex: 1 }} />

      <button onClick={aoCancelar} style={{ fontSize: 12, color: "var(--txt2)" }}>Cancelar</button>
      <button
        onClick={aoAplicar}
        disabled={!podeAplicar || aplicando}
        style={{
          height: 28, padding: "0 12px", borderRadius: 7, background: "var(--brand)",
          color: "#FFFFFF", fontSize: 12, fontWeight: 600,
          opacity: !podeAplicar || aplicando ? 0.5 : 1,
        }}
      >
        {aplicando ? "Aplicando…" : `Aplicar em ${quantos}`}
      </button>
    </div>
  )
}

/**
 * Exclusão é a única sem desfazer: linha apagada não volta e o endpoint não
 * devolve o que apagou. Por isso a confirmação exige digitar a contagem — não é
 * cerimônia, é a diferença entre apagar 3 e apagar 300 por engano.
 */
function ConfirmarExclusao({ quantos, excluindo, aoExcluir, aoCancelar }: {
  quantos: number; excluindo: boolean; aoExcluir: () => void; aoCancelar: () => void
}) {
  const [digitado, setDigitado] = useState("")
  const confere = digitado.trim() === String(quantos)

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, padding: "9px 18px",
      background: "var(--erro-soft)", borderTop: "1px solid var(--erro)",
    }}>
      <span style={{ fontSize: 12, color: "var(--erro-on-soft)" }}>
        Excluir <b>{quantos}</b> {quantos === 1 ? "pinout" : "pinouts"}.
        <b> Não há como desfazer.</b> Digite <b>{quantos}</b> para confirmar:
      </span>
      <input
        value={digitado}
        autoFocus
        onChange={(e) => setDigitado(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && confere) aoExcluir() }}
        aria-label={`Digite ${quantos} para confirmar a exclusão`}
        style={{
          width: 70, height: 28, padding: "0 9px", borderRadius: 7, fontFamily: "var(--fm)",
          border: `1px solid ${confere ? "var(--erro)" : "var(--line2)"}`,
          background: "var(--surface)", fontSize: 12.5, textAlign: "center",
        }}
      />
      <span style={{ flex: 1 }} />
      <button onClick={aoCancelar} style={{ fontSize: 12, color: "var(--txt2)" }}>Cancelar</button>
      <button
        onClick={aoExcluir}
        disabled={!confere || excluindo}
        style={{
          height: 28, padding: "0 12px", borderRadius: 7, background: "var(--erro)",
          color: "#FFFFFF", fontSize: 12, fontWeight: 600,
          opacity: !confere || excluindo ? 0.45 : 1,
        }}
      >
        {excluindo ? "Excluindo…" : `Excluir ${quantos}`}
      </button>
    </div>
  )
}
